import React from "react";
import { IoClose } from "react-icons/io5";
import { useWizardContext } from "./useWizardContext";
import Step1_BasicInfo from "./steps/Step1_BasicInfo";
import Step2_RoleSpecific from "./steps/Step2_RoleSpecific";
import Step3_BudgetAuth from "./steps/Step3_BudgetAuth";
import Step4_ContactDetails from "./steps/Step4_ContactDetails";
import { Star, MapPin, CalendarDays, Mic2, NotepadText, HardHat } from "lucide-react";

// A simple step indicator dot component
const StepDot = ({ number, isActive, isCompleted }) => {
  return (
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors z-10 relative
        ${isActive
          ? "bg-[#FF4C18] text-white"
          : isCompleted
            ? "bg-[#FF4C18] text-white"
            : "bg-gray-400 text-white"
        }`}
    >
      {number}
    </div>
  );
};

const WizardModalContent = () => {
  const { isOpen, closeWizard, step, totalSteps, isDirectQuote, professionalDetails, isSuccess, isEditMode, isLoggingIn } = useWizardContext();

  if (!isOpen) return null;

  // Render the current step component
  const renderStep = () => {
    switch (step) {
      case 1:
        return <Step1_BasicInfo />;
      case 2:
        return <Step2_RoleSpecific />;
      case 3:
        return <Step3_BudgetAuth />;
      case 4:
        return <Step4_ContactDetails />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4">
      <div
        className="bg-white rounded-[28px] sm:rounded-[32px] max-h-[92vh] min-h-[500px] sm:min-h-[550px] w-full max-w-[680px] relative flex flex-col shadow-2xl overflow-y-auto hide-scrollbar p-3.5 sm:p-5 md:p-6"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {/* Close Button */}
        <button
          onClick={closeWizard}
          className="absolute right-4 top-4 sm:right-5 sm:top-5 text-gray-400 hover:text-gray-900 transition-colors z-20 p-1"
        >
          <IoClose className="w-6 h-6" />
        </button>

        {isSuccess ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="w-24 h-24 bg-[#FF4C18] rounded-full flex items-center justify-center mb-6 shadow-lg">
              <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {isEditMode ? "Your requirement is updated!" : "Your requirement is live!"}
            </h2>
            <p className="text-gray-500 text-lg max-w-lg">
              We're matching you with the right event professionals. You'll receive proposals and updates directly in your dashboard.
            </p>
          </div>
        ) : (
          <>
            {isDirectQuote && professionalDetails ? (
              <div className="w-full pt-6 pb-3 px-6 md:px-10 border-b border-gray-100">
                <div className="flex items-center gap-5">
                  <div className="w-[72px] h-[72px] rounded-full overflow-hidden bg-gray-100 shrink-0 border border-gray-200">
                    <img
                      src={professionalDetails.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(professionalDetails.name)}`}
                      alt={professionalDetails.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col">
                    <h2 className="text-[22px] font-bold text-gray-900 leading-none mb-1.5">{professionalDetails.name}</h2>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2 text-xs font-medium">
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={12}
                            className={i < Math.round(professionalDetails.rating || 0) ? "fill-[#FF5722] text-[#FF5722]" : "text-gray-300"}
                            strokeWidth={i < Math.round(professionalDetails.rating || 0) ? 0 : 1.5}
                          />
                        ))}
                      </div>
                      <span className="text-gray-600">({professionalDetails.rating || 0}/5)</span>
                      <span className="text-gray-400">• {professionalDetails.reviews} reviews</span>
                    </div>
                    <div className="flex flex-nowrap items-center w-fit max-w-full text-[9px] sm:text-[11px] text-gray-600 font-medium border border-gray-200 rounded overflow-hidden">
                      <div className="flex items-center gap-0.5 sm:gap-1 px-1.5 py-0.5 sm:px-2 sm:py-1 capitalize shrink-0">
                        {professionalDetails.role === "Performer" && (
                          <>
                            <Mic2 size={10} className="text-[#FF5722] hidden sm:block" />
                            <Mic2 size={8} className="text-[#FF5722] sm:hidden" />
                          </>
                        )}
                        {professionalDetails.role === "Planner" && (
                          <>
                            <NotepadText size={10} className="text-[#FF5722] hidden sm:block" />
                            <NotepadText size={8} className="text-[#FF5722] sm:hidden" />
                          </>
                        )}
                        {professionalDetails.role === "Crew" && (
                          <>
                            <HardHat size={10} className="text-[#FF5722] hidden sm:block" />
                            <HardHat size={8} className="text-[#FF5722] sm:hidden" />
                          </>
                        )}
                        {professionalDetails.role}
                      </div>
                      <div className="flex items-center gap-0.5 sm:gap-1 px-1.5 py-0.5 sm:px-2 sm:py-1 border-l border-r border-gray-200 shrink-0">
                        <MapPin size={10} className="text-[#FF5722] hidden sm:block" />
                        <MapPin size={8} className="text-[#FF5722] sm:hidden" />
                        <span className="truncate max-w-[70px] sm:max-w-none">{professionalDetails.location}</span>
                      </div>
                      <div className="flex items-center gap-0.5 sm:gap-1 px-1.5 py-0.5 sm:px-2 sm:py-1 shrink-0">
                        <CalendarDays size={10} className="text-[#FF5722] hidden sm:block" />
                        <CalendarDays size={8} className="text-[#FF5722] sm:hidden" />
                        Since {professionalDetails.since}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center pt-0 pb-1.5 text-center relative w-full shrink-0">
                <h2 className="text-xl sm:text-[24px] font-bold text-gray-900 mb-0.5 tracking-tight">
                  {isLoggingIn
                    ? "Welcome Back!"
                    : step === 2
                      ? "Tell us what you need"
                      : step === 3
                        ? "Set your budget & preferences"
                        : step === 4
                          ? "Create your account"
                          : "Tell us about your event"}
                </h2>
                <p className="text-xs sm:text-sm text-gray-400 font-normal max-w-lg leading-snug">
                  {isLoggingIn
                    ? "Log in to continue posting your requirement."
                    : step === 2
                      ? "Choose the services you need for your event."
                      : step === 3
                        ? "Give professionals the details they need to understand your requirement and send a relevant proposal."
                        : step === 4
                          ? "Create your account to post your requirement and receive proposals."
                          : "Share a few details about your event to find matching professional."}
                </p>
              </div>
            )}

            <div className="flex flex-col items-center w-full flex-1">
              {/* Stepper Bar */}
              {!isDirectQuote && (
                <div className="flex items-center justify-center w-full max-w-[420px] mb-2.5 sm:mb-3 relative shrink-0">
                  {/* Background Line */}
                  <div className="absolute left-4 right-4 h-[3px] bg-gray-200 top-1/2 -translate-y-1/2 z-0 rounded-full" />

                  {/* Active Line */}
                  <div
                    className="absolute left-4 h-[3px] bg-[#FF4C18] top-1/2 -translate-y-1/2 z-0 rounded-full transition-all duration-300"
                    style={{ width: `calc(${((step - 1) / (totalSteps - 1)) * 100}% - 1rem)` }}
                  />

                  {Array.from({ length: totalSteps }).map((_, idx) => {
                    const stepNumber = idx + 1;
                    return (
                      <React.Fragment key={stepNumber}>
                        <StepDot
                          number={stepNumber}
                          isActive={step === stepNumber}
                          isCompleted={step > stepNumber}
                        />
                        {stepNumber < totalSteps && <div className="flex-1" />}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {/* Dynamic Form Content */}
              <div className="w-full text-left flex-1 flex flex-col relative min-h-[380px] sm:min-h-[420px]">
                {renderStep()}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// We separate the Modal Content from the Provider so it can consume the context
export default function RequirementPostingWizard() {
  return <WizardModalContent />;
}
